import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  SessionResponse,
  type LoginRequest,
  type PasswordChangeRequest,
  type SessionMember,
} from '@grc/shared';
import { ApiError, getJson, onSessionSignal, postJson } from './api';

// Who is logged in, for every screen (D21, D22): asked of `GET /api/auth/me` at the start, set by
// the login and the password change, ended by the logout or by any refusal that says the session
// is over. The routes' guard (Gate.tsx) sends the member where this says they belong.

/** Why the member is at the login, when the server ended their session while they used the app. */
export type SignedOutNotice = 'loggedOut' | 'disabled';

export type SessionState =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | {
      status: 'signedOut';
      notice?: SignedOutNotice;
      /**
       * The member logged out here (Log out, the shell's or the forced change's), so the next login
       * opens Home, not the page they left: it's often someone else's (the owner's choice, D28). A
       * session that ended by itself keeps the way back to the page wanted (D22).
       */
      afterLogout?: true;
    }
  | { status: 'signedIn'; member: SessionMember };

export type Session = {
  state: SessionState;
  /**
   * The password just typed at the login, when it's a one-time one: the forced change uses it as
   * the current password, so the member types only the new one. In memory only (never the URL,
   * the history or storage), and dropped once the change is made, at the logout, when the session
   * ends, or when the API says it's wrong.
   */
  oneTimePassword: string | undefined;
  login: (username: string, password: string) => Promise<SessionMember>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<SessionMember>;
  forgetOneTimePassword: () => void;
  /**
   * Profile's change of password was saved, and Profile hasn't said so yet (D26, D28, D29). Kept
   * here, in memory, because the change goes back to the Profile it was opened from, whose history
   * entry it can't write. Profile says "Password changed." once and forgets it; the logout and a
   * session's end drop it, as they drop the one-time password.
   */
  passwordChanged: boolean;
  notePasswordChanged: () => void;
  forgetPasswordChanged: () => void;
  /**
   * An admin's screen changed a member (A5): when it's the one logged in, the session takes their
   * new username and display name, so Home's welcome follows. Anyone else's changes nothing here.
   */
  memberEdited: (member: Pick<SessionMember, 'id' | 'username' | 'displayName'>) => void;
  /** Asks the server again who is logged in, after the start's call failed. */
  retry: () => void;
};

const SessionContext = createContext<Session | undefined>(undefined);

/** The member in a login's, me's or a password change's answer. */
async function memberOf(call: Promise<unknown>): Promise<SessionMember> {
  const answer = SessionResponse.safeParse(await call);
  if (!answer.success) throw new ApiError(200, 'unexpected');
  return answer.data.member;
}

/** What a refused `GET /api/auth/me` at the start means. */
function afterRefusedMe(err: unknown): SessionState {
  if (err instanceof ApiError && err.code === 'unauthenticated') return { status: 'signedOut' };
  // The guard ended a disabled member's session: the login says why.
  if (err instanceof ApiError && err.code === 'disabled') {
    return { status: 'signedOut', notice: 'disabled' };
  }
  return { status: 'failed', error: err };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [oneTimePassword, setOneTimePassword] = useState<string>();
  const [passwordChanged, setPasswordChanged] = useState(false);
  // Each start's check has a number; retry() starts the next.
  const [check, setCheck] = useState(0);

  useEffect(() => {
    let current = true;
    memberOf(getJson('/api/auth/me')).then(
      (member) => {
        if (current) setState({ status: 'signedIn', member });
      },
      (err: unknown) => {
        if (current) setState(afterRefusedMe(err));
      },
    );
    return () => {
      current = false;
    };
  }, [check]);

  // A refusal from any call while a member is logged in: the session ended (a 401, or the member
  // was disabled), or the password became a one-time one. Before anyone is logged in (the start's
  // check, a login) the caller handles its own refusal.
  useEffect(
    () =>
      onSessionSignal((signal) => {
        if (signal === 'password_change_required') {
          setState((s) =>
            s.status === 'signedIn'
              ? { status: 'signedIn', member: { ...s.member, mustChangePassword: true } }
              : s,
          );
          return;
        }
        setOneTimePassword(undefined);
        setPasswordChanged(false);
        setState((s) =>
          s.status === 'signedIn'
            ? { status: 'signedOut', notice: signal === 'disabled' ? 'disabled' : 'loggedOut' }
            : s,
        );
      }),
    [],
  );

  const login = useCallback(async (username: string, password: string) => {
    const body: LoginRequest = { username, password };
    const member = await memberOf(postJson('/api/auth/login', body));
    setOneTimePassword(member.mustChangePassword ? password : undefined);
    setState({ status: 'signedIn', member });
    return member;
  }, []);

  const logout = useCallback(async () => {
    await postJson('/api/auth/logout');
    setOneTimePassword(undefined);
    setPasswordChanged(false);
    setState({ status: 'signedOut', afterLogout: true });
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const body: PasswordChangeRequest = { currentPassword, newPassword };
    const member = await memberOf(postJson('/api/auth/password', body));
    setOneTimePassword(undefined);
    setState({ status: 'signedIn', member });
    return member;
  }, []);

  const forgetOneTimePassword = useCallback(() => setOneTimePassword(undefined), []);
  const notePasswordChanged = useCallback(() => setPasswordChanged(true), []);
  const forgetPasswordChanged = useCallback(() => setPasswordChanged(false), []);

  const memberEdited = useCallback(
    ({ id, username, displayName }: Pick<SessionMember, 'id' | 'username' | 'displayName'>) =>
      setState((s) =>
        s.status === 'signedIn' && s.member.id === id
          ? { status: 'signedIn', member: { ...s.member, username, displayName } }
          : s,
      ),
    [],
  );

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setCheck((n) => n + 1);
  }, []);

  const session = useMemo<Session>(
    () => ({
      state,
      oneTimePassword,
      login,
      logout,
      changePassword,
      forgetOneTimePassword,
      passwordChanged,
      notePasswordChanged,
      forgetPasswordChanged,
      memberEdited,
      retry,
    }),
    [
      state,
      oneTimePassword,
      login,
      logout,
      changePassword,
      forgetOneTimePassword,
      passwordChanged,
      notePasswordChanged,
      forgetPasswordChanged,
      memberEdited,
      retry,
    ],
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession() needs a <SessionProvider> around it');
  return session;
}

/** The logged-in member, for the screens behind the guard (Gate.tsx). */
export function useMember(): SessionMember {
  const { state } = useSession();
  if (state.status !== 'signedIn') throw new Error('useMember() needs a logged-in member');
  return state.member;
}
